<template>
  <div class="rf-field rf-multiple" :class="computedClass" :data-name="name">

    <!-- Slot for custom label markup -->
    <slot><div class="rf-label">{{ label }}</div></slot>

    <!-- Fallback content if there are no options -->
    <slot name="fallback" v-if="!options.length || allOptionsDisabled"></slot>

    <!-- Dummy text field to force validation if there are no options -->
    <input v-if="!options.length || allOptionsDisabled" v-show="false" type="text" v-validate="validation" v-bind="{ name }" value="" ref="input">

    <div class="rf-multiple-options" ref="optionsContainer" :class="{ 'show-focus-style': showFocusStyle }">
      <label :class="getLabelClass(option)" v-for="(option, i) in options" :tabindex="option.disabled || disabled ? -1 : 0" @keyup.space="onSpace(i)" @click="onClick($event)" @mousedown="showFocusStyle = false" @keyup.tab="showFocusStyle = true" ref="focus">
        <!-- Note: We can't use dynamic type; the value binding doesn't work. Could use a render function instead, but this is fine -->
        <!-- https://github.com/vuejs/vue/issues/3915 -->
        <!-- Radio -->
        <input type="radio" v-bind="{ name }" v-model="value" :value="getOptionValue(option)" v-validate="validation" v-if="type === 'radio'" ref="input" tabindex="-1" :disabled="option.disabled || disabled">
        <!-- Checkbox -->
        <input type="checkbox" v-bind="{ name }" v-model="value" :value="getOptionValue(option)" v-validate="validation" v-if="type ==='checkbox'" ref="input" tabindex="-1" :disabled="option.disabled || disabled">

        <!-- Custom checkbox -->
        <div class="rf-checkbox-box" v-if="type === 'checkbox'">
          <icon name="check" class="rf-icon"></icon>
        </div>

        <!-- Custom Radio -->
        <div class="rf-radio-circle" v-if="type === 'radio'"></div>

        <!-- Custom label markup -->
        <slot name="option" :label="option.label || option" :description="option.description">
          <span class="option-label">{{ option.label || option }}</span> <span class="option-description" v-if="option.description">{{ option.description }}</span>
        </slot>

        <!-- Invalid icon -->
        <transition name="fade-fast">
          <icon name="exclamation-triangle" class="rf-multiple-icon rf-icon-invalid" v-if="showInvalid && i === 0"></icon>
        </transition>
      </label>

      <!-- Errors -->
      <rf-message v-if="showInvalid">{{ errors.first(name) }}</rf-message>
    </div>
  </div>
</template>

<script>
import mixins from '@/mixins/index'
import prepareValue from '@/util/prepareValue'

export default {
  name: 'RfMultiple',
  mixins: mixins,
  props: {
    options: {
      type: Array,
      required: true
    },
    type: {
      type: String,
      required: true,
      default: 'radio',
      validator (value) {
        return value === 'radio' || value === 'checkbox'
      }
    },
    // We're ovewriting default fieldId config so it's not required
    fieldId: {
      type: String,
      default: null,
      required: false
    }
  },
  data () {
    let value = this.type === 'checkbox' ? [] : null
    return {
      value: value,
      showFocusStyle: false
    }
  },
  computed: {
    meta () {
      return this.type === 'checkbox' ? {
        wufoo: {
          fields: this.getWufooFields()
        }
      } : null
    },
    allOptionsDisabled () {
      return this.options.every(option => option.disabled)
    }
  },
  watch: {
    value: function (newVal, oldVal) {
      // Only validate if new value isn't null (or empty array, in case of checkboxes)
      if (newVal instanceof Array) {
        if (newVal.length) {
          this.validate()
        }
      } else if (newVal !== null) {
        this.validate()
      }
    }
  },
  methods: {
    getOptionValue (option) {
      if (typeof option === 'object') {
        return option.value || option.label
      }

      return option
    },
    getWufooFields () {
      // First, filter out options that don't provide a field ID
      let optionsWithId = this.options.filter(function (option) {
        return typeof option.id !== 'undefined'
      })
      // If there's some Wufoo data, return it, otherwise null
      if (optionsWithId.length) {
        return optionsWithId.map(function (option) {
          let value = option.value || option.label

          return {
            id: option.id,
            value: this.hasValue(value) ? prepareValue(value) : null
          }
        }.bind(this))
      }

      return null
    },
    onClick (e) {
      // Blur all labels on click. This is handy when one of the labels has
      // been focused by the validation methods, and we want to blur it when
      // the user makes a choice

      // IE compatible way of traversing nodeList (it doesn't support forEach())
      var list = this.$refs.optionsContainer.querySelectorAll('label')
      Array.prototype.forEach.call(list, function (label) {
        label.blur()
      })
    },
    focus () {
      this.$nextTick(function () {
        // Focus first option that's not disabled
        for (var i = 0; i < this.$refs.input.length; i++) {
          if (!this.$refs.input[i].disabled) {
            this.$refs.focus[i].focus()
            break
          }
        }
      })
    },
    getLabelClass (option) {
      let classes = {
        'is-active': this.hasValue(this.getOptionValue(option)),
        'has-description': option.description,
        'is-disabled': option.disabled }
      classes['rf-' + this.type] = true

      return classes
    },
    hasValue (value) {
      if (this.value && this.value.constructor === Array) {
        return this.value.indexOf(value) > -1
      }
      return this.value === value
    },
    onSpace (optionIndex) {
      // Programatically click the input
      var input = this.$refs.input[optionIndex]

      // We need to create different event objects for IE10/11
      // https://developer.mozilla.org/en-US/docs/Web/API/Event/initEvent#Browser_compatibility
      try {
        var event = new MouseEvent('click', {
          'view': window,
          'bubbles': false,
          'cancelable': true
        })
      } catch (e) {
        // IE 10/11
        // https://www.codeproject.com/Tips/893254/JavaScript-Triggering-Event-Manually-in-Internet-E
        event = document.createEvent('MouseEvent')
        event.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null)
      }

      input.dispatchEvent(event)
    }
  }
}
</script>



// WEBPACK FOOTER //
// RfMultiple.vue?407ace44